import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal.jsx";
import { IMAGES } from "../theme.js";
import { unsplashUrl, unsplashSrcSet } from "../lib/img.js";
import { supabase, isSupabaseConfigured } from "../lib/supabase.js";

const FALLBACK = IMAGES.portfolio.map((p, i) => ({ id: `local-${i}`, ...p }));

export default function Portfolio() {
  const [items, setItems] = useState(FALLBACK);
  const [active, setActive] = useState("Alla");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isSupabaseConfigured()) return;
      const { data, error } = await supabase
        .from("portfolio")
        .select("id,title,description,image_url,category,created_at")
        .order("created_at", { ascending: false });
      if (!cancelled && !error && data && data.length > 0) {
        setItems(data);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["Alla", ...Array.from(set)];
  }, [items]);

  const visible = useMemo(
    () =>
      active === "Alla" ? items : items.filter((i) => i.category === active),
    [items, active]
  );

  return (
    <section id="portfolio" className="relative bg-white py-24 sm:py-32">
      <div className="container-x">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-navy-700" />
                Utvalda projekt
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="section-title mt-5">
                Arbete vi är{" "}
                <span className="text-leather">stolta över.</span>
              </h2>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                  active === c
                    ? "border-navy-900 bg-navy-900 text-white"
                    : "border-navy-900/15 bg-white text-navy-700 hover:bg-sand-100"
                }`}
              >
                {c}
              </button>
            ))}
          </Reveal>
        </div>

        <motion.div
          layout
          className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {visible.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.05}>
              <motion.article
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                className="group relative overflow-hidden rounded-3xl border border-navy-900/8 bg-sand-50 shadow-soft"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={unsplashUrl(item.image_url, { w: 600, q: 65 })}
                    srcSet={unsplashSrcSet(
                      item.image_url,
                      [400, 600, 900],
                      65
                    )}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    alt={item.title}
                    width="600"
                    height="750"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-navy-950/30 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy-800 backdrop-blur">
                    {item.category}
                  </span>
                  <div className="absolute inset-x-5 bottom-5 text-white">
                    <h3 className="font-display text-2xl font-semibold leading-tight">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="mt-1.5 line-clamp-2 text-sm text-white/85">
                        {item.description}
                      </p>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">
                      Läs case
                      <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
